import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  Typography,
  Stack,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Link,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import PinIcon from '@mui/icons-material/Pin';
import PhonelinkOffIcon from '@mui/icons-material/PhonelinkOff';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LinkIcon from '@mui/icons-material/Link';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RootState } from '../../store';
import { publicAPI } from '../../services/api';

import heroImg from '../../assets/landing/hero.webp';
import shopImg from '../../assets/landing/shop.webp';
import iconCard from '../../assets/landing/icon-card.webp';
import iconBank from '../../assets/landing/icon-bank.webp';
import iconMerchant from '../../assets/landing/icon-merchant.webp';
import iconSecure from '../../assets/landing/icon-secure.webp';

const LOGO = '/img/malpay-logo.png';

const NAVY = '#0F2F52';
const BRAND = '#09486D';
const INK = '#202647';
const CYAN = '#11C1EC';
const SKY = '#4DB8F0';
const MUTED = '#5F6482';
const SOFT = '#F6F9FD';
const LINE = '#E6EBF3';
const HEADING = '"Plus Jakarta Sans Variable", "Plus Jakarta Sans", "Inter Variable", system-ui, sans-serif';

const NAV_LINKS = [
  { label: 'Home', href: '#top' },
  { label: 'Features', href: '#features' },
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Security', href: '#security' },
  { label: 'Merchants', href: '#merchants' },
  { label: 'FAQ', href: '#faq' },
];

const pill = {
  borderRadius: 999,
  fontWeight: 700,
  textTransform: 'none' as const,
  px: 3.5,
  py: 1.4,
  boxShadow: 'none',
  '&:hover': { boxShadow: 'none', transform: 'none' },
};
const skyPill = {
  ...pill,
  background: SKY,
  color: '#fff',
  '&:hover': { ...pill['&:hover'], background: '#2FA9EA' },
};
const navyPill = {
  ...pill,
  background: NAVY,
  color: '#fff',
  '&:hover': { ...pill['&:hover'], background: '#0B2440' },
};

const Section: React.FC<{ id?: string; children: React.ReactNode; sx?: object; py?: { xs: number; md: number } }> = ({
  id, children, sx, py = { xs: 8, md: 12 },
}) => (
  <Box id={id} component="section" sx={{ position: 'relative', py, scrollMarginTop: 88, overflow: 'hidden', ...sx }}>
    <Container maxWidth="lg" sx={{ position: 'relative' }}>{children}</Container>
  </Box>
);

/** Reference-style section heading: title, twin cyan bars, optional lead. */
const Heading: React.FC<{ title: string; lead?: string; light?: boolean; align?: 'left' | 'center' }> = ({
  title, lead, light, align = 'center',
}) => (
  <Box sx={{ textAlign: align, mb: { xs: 5, md: 7 } }}>
    <Typography
      variant="h2"
      component="h2"
      sx={{ color: light ? '#fff' : INK, fontSize: { xs: '1.8rem', sm: '2.1rem', md: '2.5rem' }, fontWeight: 800, lineHeight: 1.2, mb: 2 }}
    >
      {title}
    </Typography>
    <Stack direction="row" spacing={1} justifyContent={align === 'center' ? 'center' : 'flex-start'} mb={lead ? 2.5 : 0}>
      <Box sx={{ width: 22, height: 4, borderRadius: 2, bgcolor: light ? 'rgba(255,255,255,0.7)' : SKY }} />
      <Box sx={{ width: 64, height: 4, borderRadius: 2, bgcolor: light ? '#fff' : CYAN }} />
    </Stack>
    {lead && (
      <Typography sx={{ color: light ? 'rgba(255,255,255,0.85)' : MUTED, fontSize: { xs: '1rem', md: '1.1rem' }, maxWidth: 680, mx: align === 'center' ? 'auto' : 0, lineHeight: 1.7 }}>
        {lead}
      </Typography>
    )}
  </Box>
);

/** Illustrative transfer confirmation, modelled on the real Send Money screen. */
const TransferMockup: React.FC = () => (
  <Box sx={{ position: 'relative', maxWidth: 400, mx: 'auto', width: '100%', mb: { xs: 7, md: 4 } }}>
    <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 4, border: `1px solid ${LINE}`, boxShadow: '0 30px 60px -24px rgba(15,47,82,0.35)', bgcolor: '#fff' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontWeight={800} color={INK}>Transfer funds</Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ bgcolor: `${CYAN}1A`, color: BRAND, borderRadius: 999, px: 1.25, py: 0.4 }}>
          <LockIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" fontWeight={700}>PIN + OTP</Typography>
        </Stack>
      </Stack>
      <Box sx={{ p: 2.5, borderRadius: 3, color: '#fff', mb: 2, position: 'relative', overflow: 'hidden', background: `linear-gradient(135deg, ${CYAN} 0%, ${BRAND} 100%)` }}>
        <Box sx={{ position: 'absolute', right: -40, top: -40, width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.12)' }} />
        <Stack direction="row" justifyContent="space-between"><Typography variant="caption" sx={{ opacity: 0.85 }}>Debit card</Typography><CreditCardIcon fontSize="small" /></Stack>
        <Typography sx={{ fontFamily: 'monospace', letterSpacing: 3, mt: 2, fontSize: '1.1rem', color: '#fff' }}>•••• •••• •••• 4081</Typography>
        <Stack direction="row" justifyContent="space-between" mt={1.5}><Typography variant="caption" sx={{ opacity: 0.85 }}>Default card</Typography><Typography variant="caption" sx={{ opacity: 0.85 }}>VISA</Typography></Stack>
      </Box>
      <Stack spacing={1.25} mb={2}>
        <Row label="Beneficiary" value="Adaeze O. · GTBank ····2210" />
        <Row label="Amount" value="₦25,000.00" strong />
        <Row label="Reference" value="MP-4F9K2Q" />
      </Stack>
      <Stack direction="row" spacing={1} mb={2}>
        {[1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{ flex: 1, height: 44, borderRadius: 2, border: `2px solid ${i < 4 ? CYAN : '#D0D0DC'}`, display: 'grid', placeItems: 'center', fontWeight: 800, color: BRAND }}>{i < 4 ? '•' : ''}</Box>
        ))}
      </Stack>
      <Button fullWidth variant="contained" disableElevation sx={{ ...navyPill, py: 1.3 }}>Authorise with PIN</Button>
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: MUTED }}>A one-time passcode (OTP) is issued to your registered email for every transaction.</Typography>
    </Paper>
    <Paper elevation={0} sx={{ position: 'absolute', right: { xs: 8, md: -28 }, bottom: -54, p: 1.5, pr: 2, borderRadius: 3, border: `1px solid ${LINE}`, boxShadow: '0 12px 30px -10px rgba(15,47,82,0.3)', display: 'flex', alignItems: 'center', gap: 1.25, bgcolor: '#fff' }}>
      <CheckCircleIcon sx={{ color: '#00C48C' }} />
      <Box>
        <Typography fontWeight={800} fontSize="0.9rem" color={INK}>Transfer successful</Typography>
        <Typography variant="caption" color={MUTED}>Beneficiary account credited</Typography>
      </Box>
    </Paper>
  </Box>
);


const BUSINESS_TYPES = ['Retail', 'Food and hospitality', 'E-commerce', 'Professional services', 'Other'];

const WaitlistForm: React.FC = () => {
  const [form, setForm] = useState({ businessName: '', contactName: '', email: '', phone: '', businessType: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setStatus('sending');
    try {
      await publicAPI.joinMerchantWaitlist({
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        businessType: form.businessType || undefined,
      });
      setStatus('done');
    } catch (err: any) {
      setStatus('error');
      setError(err?.response?.data?.error?.message || err?.response?.data?.error || 'Your request could not be submitted. Please try again.');
    }
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2.5 },
    '& .MuiInputLabel-root': { color: MUTED },
  };

  return (
    <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 4, bgcolor: '#fff', boxShadow: '0 30px 60px -30px rgba(0,0,0,0.5)' }}>
      <Typography variant="h5" component="h3" fontWeight={800} color={INK} mb={0.5}>Join the merchant waitlist</Typography>
      <Typography variant="body2" color={MUTED} mb={3}>Tell us about your business. This information is used only to contact you about merchant onboarding.</Typography>

      {status === 'done' ? (
        <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ borderRadius: 2.5, alignItems: 'center' }}>
          You are on the waitlist. We will email you when merchant onboarding opens.
        </Alert>
      ) : (
        <Box component="form" onSubmit={submit} noValidate>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Business name" value={form.businessName} onChange={set('businessName')} required fullWidth size="medium" inputProps={{ maxLength: 120 }} sx={fieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Contact name" value={form.contactName} onChange={set('contactName')} required fullWidth inputProps={{ maxLength: 120 }} sx={fieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Business email" type="email" value={form.email} onChange={set('email')} required fullWidth inputProps={{ maxLength: 255, autoComplete: 'email' }} sx={fieldSx} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField label="Phone (optional)" type="tel" value={form.phone} onChange={set('phone')} fullWidth inputProps={{ maxLength: 32, autoComplete: 'tel' }} sx={fieldSx} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField label="Business category" select value={form.businessType} onChange={set('businessType')} fullWidth sx={fieldSx}>
                {BUSINESS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
          {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2.5 }}>{error}</Alert>}
          <Button
            type="submit" fullWidth size="large" variant="contained" disableElevation
            disabled={status === 'sending' || !form.businessName || !form.contactName || !form.email}
            sx={{ ...skyPill, mt: 3, '&.Mui-disabled': { background: '#BFE3F7', color: '#fff' } }}
          >
            {status === 'sending' ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Join the waitlist'}
          </Button>
          <Typography variant="caption" color={MUTED} display="block" textAlign="center" mt={1.5}>
            No payment details are collected on this form. Joining the waitlist does not create an account.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const Row: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
    <Typography variant="body2" color={MUTED}>{label}</Typography>
    <Typography variant="body2" fontWeight={strong ? 800 : 600} color={INK} textAlign="right">{value}</Typography>
  </Stack>
);

const FEATURES: { img: string; title: string; body: string; tag?: string }[] = [
  { img: iconCard, title: 'Card-to-account transfers', body: 'Transfer funds from your debit card to any Nigerian bank account. There is no wallet to fund, the amount is debited from your card and credited to the beneficiary\'s account.' },
  { img: iconMerchant, title: 'Merchant payments', tag: 'Coming soon', body: 'Pay merchants through a payment link, QR code or online storefront, using the same card, transaction PIN and one-time passcode.' },
  { img: iconBank, title: 'Two-factor authorisation on every transaction', body: 'Each transaction is authorised with your 4-digit transaction PIN and a single-use one-time passcode (OTP) issued to your registered email. Authenticator-app two-factor authentication (2FA) is available at login.' },
  { img: iconSecure, title: 'Your card number is never stored on our infrastructure', body: 'Card data is captured on a PCI DSS Level 1 certified payment page and is neither transmitted to nor stored by MalPay. We retain only the card scheme and last four digits, so no primary account number (PAN) exists on our systems to be compromised.' },
];

const STEPS = [
  { title: 'Create your account', body: 'Register with your email address and mobile number, then verify your email using the link or code we send.' },
  { title: 'Link your debit card', body: 'Enter your card details on the secure payment page. A one-time ₦50 card verification charge confirms the card is active.' },
  { title: 'Add a beneficiary account', body: 'Enter the beneficiary\'s account number. The account name is validated with the bank before any transfer is initiated.' },
  { title: 'Authorise with PIN and OTP', body: 'Enter the amount, review the transaction fees, then authorise with your PIN and one-time passcode. The beneficiary account is credited.' },
];

const SECURITY = [
  { icon: <PinIcon />, title: 'Two-factor authorisation on every transfer', body: 'Your transaction PIN and a single-use one-time passcode are both required. Repeated incorrect PIN attempts temporarily lock transfers, and each passcode is valid for a single use.' },
  { icon: <ReplayIcon />, title: 'Automatic reversal on failed settlement', body: 'If your card is debited but the transfer to the beneficiary\'s bank fails or is returned, the debit is reversed to your card and the transaction is recorded as reversed in your transaction history.' },
  { icon: <PhonelinkOffIcon />, title: 'An alternative when your banking app is unavailable', body: 'If your mobile banking app cannot complete a transfer but your debit card remains active, you can initiate the transfer through MalPay instead.' },
  { icon: <ReceiptLongIcon />, title: 'Transparent fees before authorisation', body: 'The transaction fee and the total amount to be debited are displayed before you enter your PIN. Every transaction is recorded in your history with a unique reference.' },
];

const MERCHANT_POINTS = [
  { icon: <LinkIcon />, text: 'Payment links to share via WhatsApp, Instagram or email' },
  { icon: <QrCode2Icon />, text: 'QR code payments at the point of sale' },
  { icon: <StorefrontIcon />, text: 'An online storefront with your products and pricing' },
  { icon: <AccountBalanceIcon />, text: 'Settlement to your bank account with a unique reference for each payment' },
];

const FAQ = [
  { q: 'Is MalPay a wallet? Do I need to fund an account first?', a: 'No. MalPay does not hold customer funds. Each transfer debits your debit card for the amount plus transaction fees and credits the beneficiary\'s bank account.' },
  { q: 'How is each transfer secured?', a: 'Every transfer is authorised with your 4-digit transaction PIN and a one-time passcode (OTP) issued to your registered email. Authenticator-app two-factor authentication (2FA) can also be enabled for login.' },
  { q: 'What are the fees?', a: 'Transaction fees are calculated per transfer and displayed in full before you enter your PIN. No amount is debited that has not been shown to you.' },
  { q: 'What happens if a transfer fails?', a: 'If your card is debited but the transfer to the beneficiary bank fails or is returned, the debit is reversed to your card. The transaction is recorded as "reversed" in your transaction history.' },
  { q: 'Which banks are supported?', a: 'Any Nigerian bank account that can receive instant transfers, including commercial banks and most microfinance banks and licensed fintech institutions.' },
];

const LandingPage: React.FC = () => {
  const { isAuthenticated, isLoading } = useSelector((s: RootState) => s.auth);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState('#top');

  // Elevation on scroll + scroll-spy for the nav links. Smooth anchor scrolling is scoped to this page.
  useEffect(() => {
    const ids = NAV_LINKS.map((l) => l.href.slice(1));
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
      const line = window.scrollY + 120;
      let current = '#top';
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= line) current = `#${id}`;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.documentElement.style.scrollBehavior = prev;
    };
  }, []);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box id="top" sx={{ bgcolor: '#fff', color: INK }}>
      {/* Navigation: white bar, logo left, links + navy Log in pill right */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: '#fff', color: INK,
          boxShadow: scrolled ? '0 8px 30px -18px rgba(15,47,82,0.35)' : 'none',
          borderBottom: scrolled ? `1px solid ${LINE}` : '1px solid transparent',
          transition: 'box-shadow .2s, border-color .2s',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 84 } }}>
            <Link component={RouterLink} to="/" sx={{ display: 'inline-flex', alignItems: 'center' }}>
              <Box component="img" src={LOGO} alt="MalPay" width={246} height={108} sx={{ height: { xs: 40, md: 50 }, width: 'auto', display: 'block' }} />
            </Link>

            <Box sx={{ flexGrow: 1 }} />

            <Stack direction="row" spacing={{ md: 3, lg: 3.5 }} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' }, mr: 3 }}>
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  underline="none"
                  aria-current={active === l.href ? 'true' : undefined}
                  sx={{
                    position: 'relative', color: active === l.href ? SKY : INK, fontWeight: 600, fontSize: '1rem', py: 0.5,
                    transition: 'color .15s', '&:hover': { color: SKY },
                    '&::after': {
                      content: '""', position: 'absolute', left: 0, right: 0, bottom: -6, height: 2, borderRadius: 1,
                      bgcolor: SKY, transform: active === l.href ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform .2s',
                    },
                  }}
                >
                  {l.label}
                </Link>
              ))}
            </Stack>

            <Button component={RouterLink} to="/login" variant="contained" disableElevation sx={{ ...navyPill, display: { xs: 'none', md: 'inline-flex' }, px: 3, py: 1.2 }}>
              Log in
            </Button>

            <IconButton aria-label="Open menu" onClick={() => setMenuOpen(true)} sx={{ display: { md: 'none' }, color: NAVY }}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={menuOpen} onClose={() => setMenuOpen(false)}>
        <Box sx={{ width: 300, maxWidth: '85vw', p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" px={1}>
            <Box component="img" src={LOGO} alt="MalPay" sx={{ height: 36 }} />
            <IconButton aria-label="Close menu" onClick={() => setMenuOpen(false)}><CloseIcon /></IconButton>
          </Stack>
          <List sx={{ mt: 1 }}>
            {NAV_LINKS.map((l) => (
              <ListItemButton key={l.href} component="a" href={l.href} selected={active === l.href} onClick={() => setMenuOpen(false)} sx={{ borderRadius: 2, '&.Mui-selected': { bgcolor: `${CYAN}1A`, color: BRAND } }}>
                <ListItemText primary={l.label} primaryTypographyProps={{ fontWeight: 700 }} />
              </ListItemButton>
            ))}
          </List>
          <Divider sx={{ my: 1 }} />
          <Box px={1} pt={1}>
            <Button component={RouterLink} to="/login" variant="contained" disableElevation fullWidth sx={navyPill}>Log in</Button>
          </Box>
        </Box>
      </Drawer>

      {/* Hero: headline left, illustration right, on white like the reference */}
      <Box component="section" sx={{ position: 'relative', overflow: 'hidden', pt: { xs: 6, md: 9 }, pb: { xs: 8, md: 12 }, bgcolor: '#fff' }}>
        <Box aria-hidden sx={{ position: 'absolute', width: 620, height: 620, borderRadius: '50%', right: -220, top: -260, background: `radial-gradient(circle, ${CYAN}22, transparent 65%)`, pointerEvents: 'none' }} />
        <Box aria-hidden sx={{ position: 'absolute', width: 420, height: 420, borderRadius: '50%', left: -200, bottom: -220, background: `radial-gradient(circle, ${SKY}1F, transparent 65%)`, pointerEvents: 'none' }} />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Grid container spacing={{ xs: 6, md: 4 }} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Typography
                variant="h1"
                component="h1"
                sx={{ fontSize: { xs: '2.4rem', sm: '3rem', md: '3.5rem' }, fontWeight: 800, lineHeight: 1.1, color: INK, mb: 3, letterSpacing: '-0.01em' }}
              >
                Pay and transfer funds with your debit card.
              </Typography>
              <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.25rem' }, color: MUTED, lineHeight: 1.65, mb: 4, maxWidth: 480 }}>
                Transfers to any Nigerian bank account and payments to merchants, authorised with your transaction PIN and a one-time passcode.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <Button component={RouterLink} to="/login" size="large" variant="contained" disableElevation endIcon={<ChevronRightIcon />} sx={{ ...skyPill, px: 4 }}>
                  Get Started
                </Button>
                <Button component={Link} href="#how-it-works" size="large" variant="text" sx={{ ...pill, color: BRAND, px: 2.5, '&:hover': { ...pill['&:hover'], bgcolor: SOFT } }}>
                  See how it works
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} rowGap={1.5} mt={5} flexWrap="wrap" useFlexGap>
                {['No wallet to fund', 'PIN and OTP on every transaction', 'Fees displayed before authorisation'].map((t) => (
                  <Stack key={t} direction="row" spacing={1} alignItems="center">
                    <CheckCircleIcon sx={{ color: '#00C48C', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={600} color={INK}>{t}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Box component="img" src={heroImg} alt="People paying and receiving money with cards and a phone" width={1107} height={747} {...{ fetchpriority: 'high' }} decoding="async" sx={{ width: '100%', height: 'auto', display: 'block', maxWidth: 720, mx: 'auto' }} />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features: four service cards with the brand's gradient icons */}
      <Section id="features" sx={{ bgcolor: SOFT }} py={{ xs: 8, md: 11 }}>
        <Heading title="What you can do with MalPay" lead="One card, one PIN, and the core capabilities you expect from a payments platform." />
        <Grid container spacing={3}>
          {FEATURES.map((f) => (
            <Grid key={f.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 3.5 }, height: '100%', borderRadius: 4, bgcolor: '#fff', border: `1px solid ${LINE}`,
                  textAlign: 'center', transition: 'transform .2s, box-shadow .2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 24px 48px -28px rgba(15,47,82,0.45)' },
                }}
              >
                <Box component="img" src={f.img} alt="" height={84} loading="lazy" decoding="async" sx={{ height: 84, width: 'auto', mx: 'auto', mb: 2.5, display: 'block' }} />
                <Typography variant="h6" component="h3" fontWeight={800} color={INK} mb={1} lineHeight={1.3}>{f.title}</Typography>
                {'tag' in f && f.tag && (
                  <Box component="span" sx={{ display: 'inline-block', fontSize: '0.72rem', fontWeight: 800, letterSpacing: 0.5, color: BRAND, bgcolor: `${CYAN}1F`, borderRadius: 999, px: 1.25, py: 0.25, mb: 1.25 }}>{f.tag}</Box>
                )}
                <Typography variant="body2" color={MUTED} lineHeight={1.7}>{f.body}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* How it works: illustration left, numbered steps right (reference layout) */}
      <Section id="how-it-works" sx={{ bgcolor: '#fff' }}>
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }} order={{ xs: 2, md: 1 }}>
            <Box component="img" src={shopImg} alt="A card terminal, a bank card and a shopper" width={992} height={916} loading="lazy" decoding="async" sx={{ width: '100%', height: 'auto', display: 'block', maxWidth: 560, mx: 'auto' }} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }} order={{ xs: 1, md: 2 }}>
            <Heading title="How it works" lead="Onboarding takes a few minutes. After that, every transfer is amount, PIN, one-time passcode." align="left" />
            <Stack spacing={3}>
              {STEPS.map((s, i) => (
                <Stack key={s.title} direction="row" spacing={2.5}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0, display: 'grid', placeItems: 'center', color: '#fff', fontWeight: 800, fontFamily: HEADING, background: `linear-gradient(135deg, ${CYAN}, ${BRAND})` }}>
                    {i + 1}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="h3" fontWeight={800} color={INK} lineHeight={1.3} mb={0.5}>{s.title}</Typography>
                    <Typography variant="body2" color={MUTED} lineHeight={1.7}>{s.body}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
            <Button component={RouterLink} to="/login" size="large" variant="contained" disableElevation endIcon={<ChevronRightIcon />} sx={{ ...skyPill, mt: 5 }}>
              Get Started
            </Button>
          </Grid>
        </Grid>
      </Section>

      {/* Security: the transfer confirmation mockup + four cards */}
      <Section id="security" sx={{ bgcolor: SOFT }}>
        <Heading title="Built around the moment you authorise" lead="Every transfer is authorised by you with two factors and recorded with a unique reference. If settlement cannot be completed after your card is debited, the debit is reversed." />
        <Grid container spacing={{ xs: 8, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <TransferMockup />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <Grid container spacing={3}>
              {SECURITY.map((t) => (
                <Grid key={t.title} size={{ xs: 12, sm: 6 }}>
                  <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: `1px solid ${LINE}`, height: '100%', bgcolor: '#fff' }}>
                    <Box sx={{ width: 46, height: 46, borderRadius: 2.5, display: 'grid', placeItems: 'center', color: BRAND, bgcolor: `${CYAN}1A`, mb: 2, '& svg': { fontSize: 26 } }}>{t.icon}</Box>
                    <Typography fontWeight={800} color={INK} mb={1} lineHeight={1.3}>{t.title}</Typography>
                    <Typography variant="body2" color={MUTED} lineHeight={1.7}>{t.body}</Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Section>

      {/* Merchants: what is coming, plus the waitlist form */}
      <Section id="merchants" sx={{ background: `radial-gradient(circle at 85% 15%, ${CYAN}55, transparent 45%), linear-gradient(160deg, #0B5580 0%, ${BRAND} 60%, #063450 100%)` }}>
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)', backgroundSize: '26px 26px', maskImage: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.9) 50%)', WebkitMaskImage: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.9) 50%)' }} />
        <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Heading title="Merchant payments are coming soon" lead="Businesses will be able to accept payments from MalPay users through payment links, QR codes at the point of sale or an online storefront, with settlement to their bank account. Join the waitlist and we will contact you when merchant onboarding opens." light align="left" />
            <Stack spacing={2}>
              {MERCHANT_POINTS.map((p) => (
                <Stack key={p.text} direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,0.12)', color: '#fff', flexShrink: 0 }}>{p.icon}</Box>
                  <Typography sx={{ color: '#fff', fontWeight: 600 }}>{p.text}</Typography>
                </Stack>
              ))}
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <WaitlistForm />
          </Grid>
        </Grid>
      </Section>

      {/* FAQ */}
      <Section id="faq" sx={{ bgcolor: '#fff' }}>
        <Heading title="Frequently asked questions" lead="Clear answers on how MalPay works, what it costs and how exceptions are handled." />
        <Box sx={{ maxWidth: 820, mx: 'auto' }}>
          {FAQ.map((f) => (
            <Accordion key={f.q} disableGutters elevation={0} sx={{ mb: 1.5, borderRadius: '16px !important', border: `1px solid ${LINE}`, bgcolor: '#fff', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: BRAND }} />} sx={{ px: { xs: 2, md: 3 }, py: 0.5 }}>
                <Typography fontWeight={800} color={INK}>{f.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: 3, pt: 0 }}>
                <Typography color={MUTED} lineHeight={1.75}>{f.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Section>

      {/* CTA */}
      <Section py={{ xs: 4, md: 8 }} sx={{ bgcolor: SOFT }}>
        <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 5, p: { xs: 4, md: 8 }, textAlign: 'center', color: '#fff', background: `linear-gradient(135deg, ${CYAN} 0%, ${BRAND} 100%)` }}>
          <Box aria-hidden sx={{ position: 'absolute', width: 320, height: 320, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.10)', top: -120, right: -80 }} />
          <Box aria-hidden sx={{ position: 'absolute', width: 220, height: 220, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)', bottom: -100, left: -60 }} />
          <Typography variant="h2" component="h2" sx={{ position: 'relative', color: '#fff', fontSize: { xs: '1.8rem', md: '2.6rem' }, fontWeight: 800, mb: 2 }}>Ready to make your first transfer?</Typography>
          <Typography sx={{ position: 'relative', color: 'rgba(255,255,255,0.9)', fontSize: { xs: '1rem', md: '1.1rem' }, mb: 4, maxWidth: 600, mx: 'auto' }}>Create a free account, link your card once, and transfer to any bank account in minutes.</Typography>
          <Stack direction="row" justifyContent="center" sx={{ position: 'relative' }}>
            <Button component={RouterLink} to="/login" size="large" variant="contained" disableElevation endIcon={<ArrowForwardIcon />} sx={{ ...pill, px: 4, background: '#fff', color: BRAND, '&:hover': { ...pill['&:hover'], background: '#E8F8FD' } }}>
              Get Started
            </Button>
          </Stack>
          <Typography variant="body2" sx={{ position: 'relative', color: 'rgba(255,255,255,0.8)', mt: 3 }}>Available on the web today. Android and iOS applications are in development.</Typography>
        </Box>
      </Section>

      {/* Footer */}
      <Box component="footer" sx={{ borderTop: `1px solid ${LINE}`, py: 6, bgcolor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box component="img" src={LOGO} alt="MalPay" width={246} height={108} loading="lazy" sx={{ height: 44, width: 'auto', mb: 2, display: 'block' }} />
              <Typography variant="body2" color={MUTED} lineHeight={1.7} maxWidth={380}>
                Card-based transfers and merchant payments for Nigeria. MalPay does not hold customer funds.
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FooterCol title="Product" links={NAV_LINKS.slice(1).map((l) => ({ label: l.label, href: l.href }))} />
            </Grid>
            <Grid size={{ xs: 6, md: 2 }}>
              <FooterCol title="Account" links={[
                { label: 'Log in', to: '/login' },
                { label: 'Create account', to: '/register' },
                { label: 'Forgot password', to: '/forgot-password' },
              ]} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <Typography fontWeight={800} color={INK} mb={1.5}>Card processing</Typography>
              <Typography variant="body2" color={MUTED} lineHeight={1.7}>
                Card payments are processed by Paystack, a licensed payment service provider, which collects and stores card
                data on its own PCI DSS compliant systems. MalPay does not store primary account numbers. Full terms are provided at registration.
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 4 }} />
          <Typography variant="body2" color={MUTED}>© {new Date().getFullYear()} MalPay. All rights reserved.</Typography>
        </Container>
      </Box>
    </Box>
  );
};

const FooterCol: React.FC<{ title: string; links: { label: string; href?: string; to?: string }[] }> = ({ title, links }) => (
  <>
    <Typography fontWeight={800} color={INK} mb={1.5}>{title}</Typography>
    <Stack spacing={1}>
      {links.map((l) =>
        l.to ? (
          <Link key={l.label} component={RouterLink} to={l.to} underline="hover" variant="body2" sx={{ color: MUTED }}>{l.label}</Link>
        ) : (
          <Link key={l.label} href={l.href} underline="hover" variant="body2" sx={{ color: MUTED }}>{l.label}</Link>
        ),
      )}
    </Stack>
  </>
);

export default LandingPage;
